import { BREEZE_COPILOT } from 'chatspot-core/types/CopilotAssistantTypes';
import { TOPIC_NAMES } from 'copilot-toolkit/types/Channel';
import Raven from 'raven-js';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAssistantType } from './assistants/selectors/assistants';
import { selectAblyChannelConnectingState, selectFetchStatus } from './chat/selectors/chatv2Selectors';
import { useAssistantSwitcher } from './hooks/useAssistantSwitcher';
import { selectCreateThreadStatus } from './threads/selectors/threadsSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
/*
We have this ignore topics array because we dont always want to switch breeze assiatant, the example case is if the assistant is trying to start a new thread
we should start a new thread in the assistant itself. Similarly there will be other actions as well where we want to not switch the assitant back to BREEZE_COPILOT
*/
const IGNORE_TOPICS_FOR_ASSISTANTS = [TOPIC_NAMES.START_NEW_CHAT];
const JobQueueContext = /*#__PURE__*/createContext(null);
export const JobQueueProvider = ({
  children
}) => {
  const [queue, setQueue] = useState([]);
  const topicHandlersRef = useRef(new Map());
  const [isThreadDataLoaded, setIsThreadDataLoaded] = useState(false);
  const [isAblyChannelConnected, setIsAblyChannelConnected] = useState(false);
  const {
    updateAssistantType
  } = useAssistantSwitcher();
  const ablyChannelConnectingState = useSelector(selectAblyChannelConnectingState);
  const assistantType = useSelector(selectAssistantType);
  const fetchStatus = useSelector(selectFetchStatus);
  const createThreadStatus = useSelector(selectCreateThreadStatus);
  const isReadyToExecuteQueue = useMemo(() => {
    return ablyChannelConnectingState === 'success' && assistantType === BREEZE_COPILOT && fetchStatus === 'success' && isThreadDataLoaded && isAblyChannelConnected && createThreadStatus !== 'loading' && queue.length > 0;
  }, [ablyChannelConnectingState, assistantType, fetchStatus, isThreadDataLoaded, isAblyChannelConnected, createThreadStatus, queue.length]);
  const registerTopicHandlerForQueue = useCallback((topic, handler) => {
    topicHandlersRef.current.set(topic, handler);
  }, []);
  const executeJobsAndClearQueue = useCallback(() => {
    queue.forEach(job => {
      const currentHandler = topicHandlersRef.current.get(job.topic);
      if (currentHandler) {
        try {
          currentHandler(job.payload);
        } catch (error) {
          Raven.captureException(error, {
            extra: {
              component: 'JobQueueProvider',
              action: 'executeJobsAndClearQueue',
              topic: job.topic
            }
          });
        }
      }
    });
    setQueue([]);
  }, [queue]);
  const enqueueJob = useCallback((topic, payload) => {
    const queuedJob = {
      topic,
      payload
    };
    setQueue(prevQueue => [...prevQueue, queuedJob]);
  }, []);
  const switchToBreezeAssistant = useCallback(() => {
    return new Promise(resolve => {
      // If already using BREEZE_COPILOT, resolve immediately
      if (assistantType === BREEZE_COPILOT) {
        return resolve();
      }

      // Switch to BREEZE_COPILOT and wait for the change to settle
      updateAssistantType(BREEZE_COPILOT);
      resolve();
    });
  }, [assistantType, updateAssistantType]);
  useEffect(() => {
    if (isReadyToExecuteQueue) {
      executeJobsAndClearQueue();
    } else if (assistantType !== BREEZE_COPILOT && queue.length > 0 && !queue.find(job => IGNORE_TOPICS_FOR_ASSISTANTS.includes(job.topic))) {
      switchToBreezeAssistant().catch(error => {
        Raven.captureException(error, {
          extra: {
            action: 'switchToBreezeAssistant',
            component: 'JobQueueProvider'
          }
        });
      });
    }
  }, [isReadyToExecuteQueue, assistantType, queue.length, executeJobsAndClearQueue, switchToBreezeAssistant, queue]);
  const contextValue = {
    enqueueJob,
    isThreadDataLoaded,
    registerTopicHandlerForQueue,
    setIsAblyChannelConnected,
    setIsThreadDataLoaded
  };
  return /*#__PURE__*/_jsx(JobQueueContext.Provider, {
    value: contextValue,
    children: children
  });
};
export const useJobQueue = () => {
  const context = useContext(JobQueueContext);
  if (!context) {
    throw new Error('useJobQueue must be used within a JobQueueProvider');
  }
  return context;
};
export default JobQueueProvider;